/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Button, Form, FormGroup, Input, Label, FormFeedback, FormText } from 'reactstrap';
import LoadingPlaceholder from '@components/LoaderContainer/LoadingPlaceholder';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import * as auth from '../../actions/auth';
import { RouteInterface } from '../../types/routes';
import { generateUrl } from '../../routes/util';
import { BaseStepCompProps } from './types';
import { getDefaultConfig } from '../../config/app-config';
import { isPasswordValid } from '@utils/password';
import { isValidEmail } from '@utils/index';

interface SignUpFormProps extends BaseStepCompProps {
}

const termsAndConditionsRoute: RouteInterface = {
  id: 'terms_and_conditions',
  label: 'Terms and conditions',
  path: '/legal-terms-and-conditions',
};

const ReactPasswordStrength = React.lazy(() => import('../../components/react-password-strength'));

const SignUpForm = (props: SignUpFormProps) => {
  const { updateForm, formData, handlePrevious } = props;

  const [isCheckboxChecked, setCheckbox] = useState(false);
  const currentUser = useSelector((state: RootState) => state.currentUser);
  const dispatch = useDispatch();

  const passwordMatch = formData.password.length > 0 && formData.password === formData.rPassword;
  const isPassValid = passwordMatch && isPasswordValid(formData.password);
  const isEmailValid = isValidEmail(formData.email);
  const isNameValid = formData.firstName && formData.surname;

  const requiresTermsAndConditions = getDefaultConfig().branding.ctlApp.requiresTermsAndConditions !== false;
  const isCheckboxValid = !requiresTermsAndConditions || isCheckboxChecked;
  const isFormValid = isNameValid && isEmailValid && isPassValid && isCheckboxValid;

  const handleLogout = () => dispatch(auth.logout());

  const handleNext = (e: React.ChangeEvent<any>) => {
    e.preventDefault()
    if (isFormValid) {
      props.handleNext();
    }
  };

  return <>
    <div className='d-lg-flex justify-content-between onboarding-form'>
      <aside className='title-container'>
        <div className='h1'>Your details</div>
      </aside>
      <section className='form-container'>
        {currentUser.loaded ?
          <div className='mt-5'>
            <h3>You are currently logged in as <strong>{currentUser.data.firstName} {currentUser.data.surname}</strong>.</h3>
            <p>You must logout to continue.</p>
            <div className='mt-5 text-right'>
              <Button color='primary' onClick={handleLogout}>
                <i className='fa fa-sign-out-alt mr-2' />
                Click here to logout</Button>
            </div>
          </div>
          :
          <React.Suspense fallback={<LoadingPlaceholder height={600} />}>
            <Form className='my-5' >
              <div className='row'>
                <div className='col'>
                  <FormGroup>
                    <Label for='firstName' >First Name</Label>
                    <Input
                      placeholder='First name'
                      valid={formData.firstName.length > 0}
                      type='text'
                      required
                      name='firstName'
                      defaultValue={formData.firstName}
                      onChange={(e) => updateForm('firstName', e.target.value)}
                    />
                  </FormGroup>
                </div>
                <div className='col'>
                  <FormGroup>
                    <Label for='surname' >Last Name</Label>
                    <Input
                      placeholder='Last name'
                      valid={formData.surname.length > 0}
                      type='text'
                      required
                      name='surname'
                      defaultValue={formData.surname}
                      onChange={(e) => updateForm('surname', e.target.value)}
                    />
                  </FormGroup>
                </div>
              </div>

              <div className='row mt-3'>
                <div className='col'>
                  <FormGroup>
                    <Label for='email'>E-mail</Label>
                    <Input
                      placeholder='Email'
                      invalid={formData.email.length > 0 && !isEmailValid}
                      valid={formData.email.length > 0 && isEmailValid}
                      type='email'
                      required
                      defaultValue={formData.email}
                      name='email'
                      onChange={(e) => updateForm('email', e.target.value)}
                    />
                    <FormFeedback tooltip>Please ensure the e-mail is valid</FormFeedback>
                  </FormGroup>
                </div>
              </div>

              <div className='row mt-3'>
                <div className='col'>
                  <FormGroup>
                    <Label for='password'>Password</Label>
                    <ReactPasswordStrength
                      style={{ width: '100%' }}
                      required
                      minLength={10}
                      minScore={2}
                      defaultValue={formData.password}
                      changeCallback={(option) => updateForm('password', option.password)}
                      inputProps={{ required: true, name: 'password', autoComplete: 'new-password', placeholder: 'Password' }}
                    />
                    <FormText>
                      Min. 10 chars, lowercase/uppercase, number & special character
                    </FormText>
                  </FormGroup>
                </div>
              </div>

              <div className='row mt-3'>
                <div className='col'>

                  <FormGroup>
                    <Label for='rPassword'>Repeat Password</Label>
                    <ReactPasswordStrength
                      style={{ width: '100%' }}
                      required
                      minLength={10}
                      minScore={2}
                      defaultValue={formData.rPassword}
                      changeCallback={(option) => updateForm('rPassword', option.password)}
                      inputProps={{ required: true, name: 'rPassword', autoComplete: 'new-password', placeholder: 'Confirm Password' }}
                      invalid={formData.rPassword.length > 0 && !passwordMatch}
                    />
                  </FormGroup>
                </div>
              </div>

              <div className='row mt-3'>
                <div className='col'>
                  <FormGroup>
                    <Label for='telephone'>Telephone number (optional)</Label>
                    <Input
                      placeholder='Telephone Number'
                      type='text'
                      defaultValue={formData.telephoneNumber}
                      name='telephoneNumber'
                      onChange={(e) => updateForm('telephoneNumber', e.target.value)}
                    />
                    <FormText>We would love to support your reporting journey. Please provide your phone number if you would like us to call you about this.</FormText>
                  </FormGroup>
                </div>
              </div>

              {requiresTermsAndConditions ? (
                <div className='row mt-3'>
                  <div className='col'>
                    <FormGroup check>
                      <Input type='checkbox' onChange={() => setCheckbox(!isCheckboxChecked)} checked={isCheckboxChecked} required className='checkbox' />
                      <Label check onClick={() => setCheckbox(!isCheckboxChecked)}>
                        I have read and agree to the <a target='_blank' rel='noopener noreferrer' href={generateUrl(termsAndConditionsRoute)}>
                          {termsAndConditionsRoute.label}
                        </a>
                      </Label>
                    </FormGroup>
                  </div>
                </div>
              ) : null}
            </Form>
            <div className='mt-3 d-flex'>
              <Button outline className='mr-auto' onClick={handlePrevious}><i className='far fa-angle-left mr-2' />Back</Button>
              <Button color='primary' onClick={handleNext} disabled={!isFormValid} >Next<i className='far fa-angle-right ml-2' /></Button>
            </div>
          </React.Suspense>
        }
      </section>
    </div>
  </>
}

export default SignUpForm;
